import { useNavigate } from "react-router-dom";
import { Modal } from "./Modal";
import { Button } from "./Button";

export interface UpgradePromptProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Current child limit for the subscription */
  currentLimit: number;
}

/**
 * Modal shown when a parent tries to add more children than their subscription allows.
 * Encourages upgrading to a higher tier with friendly messaging.
 *
 * @example
 * <UpgradePrompt
 *   isOpen={showUpgrade}
 *   onClose={() => setShowUpgrade(false)}
 *   currentLimit={1}
 * />
 */
export function UpgradePrompt({
  isOpen,
  onClose,
  currentLimit,
}: UpgradePromptProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Want More Profiles?"
      size="sm"
    >
      <div className="flex flex-col items-center gap-6 text-center">
        {/* Decorative illustration */}
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-magic-100 to-magic-200 flex items-center justify-center">
            <span className="text-5xl" role="img" aria-label="Star sparkle">
              ✨
            </span>
          </div>
          {/* Floating sparkles */}
          <div className="absolute -top-2 -right-2 w-8 h-8 text-sunshine-400 animate-float">
            <SparkleIcon />
          </div>
          <div className="absolute -bottom-1 -left-3 w-6 h-6 text-fairy-400 animate-float" style={{ animationDelay: "0.5s" }}>
            <SparkleIcon />
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <p className="font-body text-body-lg text-neutral-700">
            Your current plan allows{" "}
            <span className="font-bold text-magic-600">
              {currentLimit} child profile{currentLimit === 1 ? "" : "s"}
            </span>
            .
          </p>
          <p className="font-body text-body-md text-neutral-500">
            Upgrade to add more children and unlock all learning adventures!
          </p>
        </div>

        {/* Benefits preview */}
        <div className="w-full p-4 bg-gradient-to-br from-sunshine-50 to-magic-50 rounded-2xl border-2 border-sunshine-200">
          <p className="font-display font-bold text-body-lg text-neutral-800 mb-3">
            Family Plan includes:
          </p>
          <ul className="space-y-2 text-left">
            <li className="flex items-center gap-3">
              <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0" />
              <span className="font-body text-body-md text-neutral-700">
                Up to 4 child profiles
              </span>
            </li>
            <li className="flex items-center gap-3">
              <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0" />
              <span className="font-body text-body-md text-neutral-700">
                All disciplines & levels
              </span>
            </li>
            <li className="flex items-center gap-3">
              <CheckIcon className="w-5 h-5 text-meadow-500 flex-shrink-0" />
              <span className="font-body text-body-md text-neutral-700">
                Progress reports for parents
              </span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 w-full pt-2">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleUpgrade}
          >
            View Plans
          </Button>

          <Button
            variant="ghost"
            size="md"
            fullWidth
            onClick={onClose}
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Check mark icon */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

/** Sparkle icon */
function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

export default UpgradePrompt;
